import { useEffect, useSyncExternalStore } from 'react';
import { Icon } from './Icon';
import { ds, useDs } from './strings';

// ── Adaptive Theme System (THEME-SYSTEM.md §4–§6) ──
// O tema vive num único sítio: o atributo `data-theme` do <html>. Trocar é UMA
// mutação de atributo — o browser recalcula estilo, não há re-render de árvore.
// Ordem de resolução (§5): escolha guardada → CLARO (o modo de origem do
// produto; o escuro é opt-in e fica guardado). O sistema operativo não decide. O index.html já pinta o atributo ANTES do React;
// este módulo só continua a partir daí.
//
// O estado é um STORE de módulo (useSyncExternalStore): o toggle da topbar, a
// linha «Aparência» do /conta e qualquer futuro consumidor leem e mudam o
// MESMO tema — sem contexto, sem provider, sem drift entre instâncias.

export type ThemeName = 'dark' | 'light';

function readSaved(): ThemeName | null {
  // try/catch obrigatório: em modo privado do Safari o localStorage lança — e
  // um tema não pode derrubar a app.
  try {
    const t = localStorage.getItem('mds-theme');
    return t === 'light' || t === 'dark' ? t : null;
  } catch {
    return null;
  }
}

export function resolveTheme(): ThemeName {
  return readSaved() ?? 'light';
}

// ── Store ────────────────────────────────────────────────────────────────────
let current: ThemeName = typeof window === 'undefined' ? 'light' : resolveTheme();
let explicit = typeof window === 'undefined' ? false : readSaved() !== null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

// Ponto de SINCRONIZAÇÃO com o backend — injetado pela app no arranque
// (PATCH /users/me/preferences {theme}). O design system não conhece as APIs
// das apps (fronteira de camadas); conhece apenas «alguém quer saber quando a
// pessoa escolhe». Fire-and-forget: a rede nunca decide a cor de um ecrã.
let persistor: ((theme: ThemeName | null) => void) | null = null;
export function setThemePersistor(fn: ((theme: ThemeName | null) => void) | null) {
  persistor = fn;
}

let xfade: ReturnType<typeof setTimeout> | undefined;

// `persist: false` quando a mudança vem do SISTEMA — só a escolha explícita do
// utilizador fica guardada; é isso que decide se continuamos a segui-lo (§5).
export function applyTheme(theme: ThemeName, animate: boolean, persist: boolean) {
  const root = document.documentElement;
  if (animate) {
    // A travessia é a ÚNICA janela em que a cor transita (320 ms). Fora dela
    // cada controlo mantém os seus 120 ms — filtrar uma lista não «desliza» cores.
    root.classList.add('theme-crossing');
    clearTimeout(xfade);
    xfade = setTimeout(() => root.classList.remove('theme-crossing'), 320);
  }
  root.setAttribute('data-theme', theme);
  if (persist) {
    try {
      localStorage.setItem('mds-theme', theme);
    } catch {
      /* modo privado */
    }
    explicit = true;
  }
  current = theme;
  emit();
}

// Preferência vinda do SERVIDOR (/users/me → preferences.theme): adota-se
// apenas quando este dispositivo ainda não tem escolha própria — a escolha
// local é sempre mais recente do que a última sincronização conhecida (§5).
// Grava no localStorage para a PRÓXIMA primeira pintura já nascer certa.
export function adoptServerTheme(theme: unknown) {
  if (theme !== 'dark' && theme !== 'light') return;
  if (readSaved()) return;
  if (theme === current) {
    // Já estamos na cor certa por acaso (sistema coincide) — só fixa a escolha.
    try { localStorage.setItem('mds-theme', theme); } catch { /* modo privado */ }
    explicit = true;
    emit();
    return;
  }
  applyTheme(theme, true, true);
}

function toggleTheme() {
  const next: ThemeName = current === 'dark' ? 'light' : 'dark';
  applyTheme(next, true, true);
  // Autenticado: a preferência segue o utilizador entre dispositivos.
  persistor?.(next);
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, () => current, () => 'light' as ThemeName);
  return { theme, isDark: theme === 'dark', explicit, toggle: toggleTheme };
}

/**
 * Liga o tema ao servidor. Chamado UMA vez, na concha de cada app.
 *
 * Os dois sentidos da sincronização (§5), e a razão de cada um:
 *  - subir  — escolher grava `PATCH /users/me/preferences`; fire-and-forget,
 *             porque uma falha de rede não pode desfazer uma cor já pintada.
 *  - descer — o perfil traz `preferences.theme` e adota-se… só se este
 *             dispositivo ainda não tiver escolha própria. A escolha local é
 *             mais recente do que a última sincronização conhecida; sobrepô-la
 *             faria o ecrã «piscar de volta» ao tema do outro telemóvel.
 *
 * O design system fica ignorante das APIs: quem chama traz o seu cliente HTTP.
 */
export function useThemeSync({ persist, serverTheme }: {
  persist: (theme: ThemeName | null) => void;
  serverTheme?: unknown;
}) {
  useEffect(() => {
    setThemePersistor(persist);
    return () => setThemePersistor(null);
    // `persist` é estável na prática (módulo de API); relê-lo a cada render só
    // trocaria a mesma função por outra igual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { adoptServerTheme(serverTheme); }, [serverTheme]);
}

// O par de ícones coexiste, sobreposto num contentor de 16px; trocam por
// opacity + rotate(±90°) + scale(.6→1) em 300 ms — nunca por troca de nó, que
// daria um salto (§4). As cores são as tintas «escrever» (--ink-*), válidas
// nos dois temas.
export function ThemeGlyph({ isDark, size = 16 }: { isDark: boolean; size?: number }) {
  const ease = 'opacity var(--t-slow) cubic-bezier(.4,0,.2,1), transform var(--t-slow) cubic-bezier(.4,0,.2,1)';
  return (
    <span style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'grid', placeItems: 'center' }}>
      <Icon name="sun" size={size} style={{ position: 'absolute', color: 'var(--ink-warning)', transition: ease,
        opacity: isDark ? 0 : 1, transform: isDark ? 'rotate(-90deg) scale(.6)' : 'rotate(0deg) scale(1)' }} />
      <Icon name="moon" size={size} style={{ position: 'absolute', color: 'var(--ink-accent)', transition: ease,
        opacity: isDark ? 1 : 0, transform: isDark ? 'rotate(0deg) scale(1)' : 'rotate(90deg) scale(.6)' }} />
    </span>
  );
}

export function themeAria(isDark: boolean): string {
  if (isDark) return ds('ds.themeDarkAria', 'Tema escuro ativo. Mudar para claro.');
  return ds('ds.themeLightAria', 'Tema claro ativo. Mudar para escuro.');
}


// A pill de 40px da barra do topo (desktop/HQ). O rótulo anuncia o estado
// ATUAL — um ícone sozinho não diz se representa o estado ou o destino.
export function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  const dsx = useDs();
  const aria = themeAria(isDark);
  return (
    <button type="button" className="theme-toggle" onClick={toggle}
      role="switch" aria-checked={isDark} aria-label={aria} title={aria}>
      <ThemeGlyph isDark={isDark} />
      <span>{isDark ? dsx('ds.dark', 'Escuro') : dsx('ds.light', 'Claro')}</span>
    </button>
  );
}
